import { ReactNode } from 'react';

import { Button, IconButton } from '@fly/core';
import { PaginationState, Table } from '@tanstack/react-table';
import classNames from 'classnames';
import { t } from 'i18next';

import {
  CursorPagination,
  CursorPaginationDirection,
  DataProcessing,
  PaginationType,
} from '../../TanstackTable.types';

export interface PaginationProps<T> {
  table: Table<T>;
  paginationType: PaginationType;
  pagination: CursorPagination | PaginationState | null;
  setPagination: (pagination: CursorPagination | PaginationState) => void;

  // Configuration & State
  dataProcessing?: DataProcessing;
  paginationKey?: keyof T;

  // Cursor State
  prevDisabled?: boolean;
  nextDisabled?: boolean;
  nextCursor?: string | number | null;
  prevCursor?: string | number | null;

  // Customization
  className?: string;
  nextContent?: ReactNode;
  prevContent?: ReactNode;
  renderPageItem?: (props: {
    page: number | string;
    isActive: boolean;
    onClick: () => void;
  }) => ReactNode;
}

// --- Helper Functions ---

const getOffsetPaginationIndex = <T,>(
  table: Table<T>,
  dataProcessing: DataProcessing
): number => {
  if (dataProcessing === 'client') {
    return table.getState().pagination.pageIndex + 1;
  }
  return table.getState().pagination.pageIndex;
};

const getPaginationRange = (currentPage: number, totalPages: number) => {
  const delta = 1;
  const range: Array<number> = [];
  const rangeWithDots: (number | string)[] = [];

  for (let i = 1; i <= totalPages; i++) {
    if (
      i <= 3 ||
      i > totalPages - 3 ||
      (i >= currentPage - delta && i <= currentPage + delta)
    ) {
      range.push(i);
    }
  }

  let l;
  for (const i of range) {
    if (l) {
      if (i - l === 2) {
        rangeWithDots.push(l + 1);
      } else if (i - l !== 1) {
        rangeWithDots.push('...');
      }
    }
    rangeWithDots.push(i);
    l = i;
  }

  if (!rangeWithDots.length) {
    return [1];
  }

  return rangeWithDots;
};

const CursorPaginationView = <T,>({
  setPagination,
  prevDisabled,
  nextDisabled,
  prevCursor,
  nextCursor,
  nextText,
  prevText,
}: PaginationProps<T> & { nextText: string; prevText: string }) => {
  return (
    <>
      <Button
        data-testid='cursor-prev'
        variant='ghost'
        size='1'
        color='accent'
        disabled={prevDisabled}
        onClick={() => {
          setPagination({
            cursor_direction: CursorPaginationDirection.PREV,
            cursor: prevCursor ? String(prevCursor) : '',
          });
        }}
        iconStart='arrow_chevron_left'
      >
        {prevText}
      </Button>
      <Button
        data-testid='cursor-next'
        variant='ghost'
        size='1'
        color='accent'
        disabled={nextDisabled}
        onClick={() => {
          setPagination({
            cursor_direction: CursorPaginationDirection.NEXT,
            cursor: nextCursor ? String(nextCursor) : '',
          });
        }}
        iconEnd='arrow_chevron_right'
      >
        {nextText}
      </Button>
    </>
  );
};

const OffsetPaginationView = <T,>({
  table,
  pagination,
  setPagination,
  dataProcessing = 'server',
  prevDisabled,
  renderPageItem,
}: PaginationProps<T> & { nextText: string; prevText: string }) => {
  const { pageIndex, pageSize } = pagination as PaginationState;
  const totalPages = table.getPageCount();
  const pages = getPaginationRange(
    table.getState().pagination.pageIndex,
    totalPages
  );
  const activeIndex = getOffsetPaginationIndex(table, dataProcessing);

  return (
    <>
      <IconButton
        variant='ghost'
        size='2'
        color='neutral'
        className='bold bg-transparent!'
        disabled={prevDisabled || pageIndex <= 1}
        onClick={() => {
          setPagination({
            pageIndex: pageIndex - 1,
            pageSize: pageSize,
          });
        }}
        icon='arrow_chevron_left'
        iconSize={24}
      />
      <div className='gap-2 flex'>
        {pages.map((page, index) => {
          const isDots = page.toString() === '...';
          const isActive = activeIndex === Number(page);
          const onClick = () => {
            if (!isDots) {
              setPagination({
                pageIndex: Number(page),
                pageSize: pageSize,
              });
            }
          };

          if (renderPageItem) {
            return renderPageItem({ page, isActive, onClick });
          }

          return (
            <Button
              key={`page-${index}`}
              color={isActive ? 'accent' : 'neutral'}
              variant='ghost'
              className={classNames(
                'px-2 py-1 rounded rounded-fly-3 transition-colors',
                isDots ? 'text-slate-11' : 'text-accent-a11',
                {
                  'font-bold bg-accentA-4': isActive,
                  'cursor-default': isDots,
                }
              )}
              disabled={isDots}
              onClick={onClick}
            >
              {page}
            </Button>
          );
        })}
      </div>
      <IconButton
        variant='ghost'
        size='3'
        className='bg-transparent!'
        color='neutral'
        disabled={pageIndex >= totalPages}
        onClick={() => {
          setPagination({
            pageIndex: pageIndex + 1,
            pageSize: pageSize,
          });
        }}
        icon='arrow_chevron_right'
        iconSize={24}
      />
    </>
  );
};

// --- Main Component ---

export function Pagination<T>(props: PaginationProps<T>) {
  const { paginationType, className } = props;

  if (paginationType === 'none') {
    return null;
  }

  // Common View Props
  const viewProps = {
    ...props,
    nextText: t('app.common.table.pagination.next', { defaultValue: 'Next' }),
    prevText: t('app.common.table.pagination.prev', {
      defaultValue: 'Previous',
    }),
  };

  return (
    <div
      className={classNames(
        'gap-2 mt-fly-5 flex items-center justify-center px-fly-3 py-fly-2',
        className
      )}
    >
      {paginationType === 'cursor' ? (
        <CursorPaginationView {...viewProps} />
      ) : (
        <OffsetPaginationView {...viewProps} />
      )}
    </div>
  );
}
